// Small self-contained UI widgets for the asset preview scene (buttons, toggles,
// cyclers, panels). Deliberately independent from src/ui so the preview keeps
// working while the UI kit is being written or changed.

import Phaser from 'phaser';
import { FONT } from '../../config/constants';
import { COLORS, css } from '../../config/palette';

export const PV = {
  W: 1920,
  H: 1080,
  headerH: 64,
  sideW: 360,
  pad: 20,
} as const;

export interface TextOpts {
  font?: keyof typeof FONT;
  size?: number;
  color?: number;
  weight?: number;
  spacing?: number;
  originX?: number;
  originY?: number;
  alpha?: number;
}

/** Text with the game's font stacks. */
export function label(scene: Phaser.Scene, x: number, y: number, text: string, o: TextOpts = {}): Phaser.GameObjects.Text {
  const t = scene.add.text(x, y, text, {
    fontFamily: FONT[o.font ?? 'body'],
    fontSize: `${o.size ?? 18}px`,
    fontStyle: String(o.weight ?? 600),
    color: css(o.color ?? COLORS.uiText),
  });
  t.setOrigin(o.originX ?? 0, o.originY ?? 0);
  if (o.spacing) t.setLetterSpacing(o.spacing);
  if (o.alpha !== undefined) t.setAlpha(o.alpha);
  return t;
}

/** Chamfered panel outline + fill drawn into a Graphics. */
export function drawPanel(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  o: { fill?: number; fillAlpha?: number; border?: number; borderAlpha?: number; chamfer?: number; accent?: number } = {},
): void {
  const c = o.chamfer ?? 10;
  const pts = [
    new Phaser.Math.Vector2(x + c, y),
    new Phaser.Math.Vector2(x + w, y),
    new Phaser.Math.Vector2(x + w, y + h - c),
    new Phaser.Math.Vector2(x + w - c, y + h),
    new Phaser.Math.Vector2(x, y + h),
    new Phaser.Math.Vector2(x, y + c),
  ];
  g.fillStyle(o.fill ?? COLORS.uiPanel, o.fillAlpha ?? 0.9);
  g.fillPoints(pts, true);
  g.lineStyle(1, o.border ?? COLORS.uiBorder, o.borderAlpha ?? 1);
  g.strokePoints(pts, true);
  if (o.accent !== undefined) {
    g.lineStyle(2, o.accent, 0.9);
    g.lineBetween(x + c, y, x + c + Math.min(64, w * 0.3), y);
  }
}

export interface ButtonOpts {
  w?: number;
  h?: number;
  accent?: number;
  size?: number;
  /** Small key hint drawn on the right ("Z"). */
  hint?: string;
  /** Toggle buttons render a lit state. */
  toggle?: boolean;
  align?: 'left' | 'center';
}

/** Clickable chamfered button with hover / active / disabled states. */
export class Button {
  readonly root: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Graphics;
  private text: Phaser.GameObjects.Text;
  private hintText: Phaser.GameObjects.Text | null = null;
  private hover = false;
  private _active = false;
  private _enabled = true;
  readonly w: number;
  readonly h: number;
  private accent: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    private onClick: () => void,
    private o: ButtonOpts = {},
  ) {
    this.w = o.w ?? 160;
    this.h = o.h ?? 36;
    this.accent = o.accent ?? COLORS.volt;
    this.root = scene.add.container(x, y);
    this.bg = scene.add.graphics();
    const left = o.align === 'left';
    this.text = label(scene, left ? 12 : this.w / 2, this.h / 2, text, {
      font: 'display',
      size: o.size ?? 15,
      weight: 600,
      spacing: 1.5,
      originX: left ? 0 : 0.5,
      originY: 0.5,
    });
    this.root.add([this.bg, this.text]);
    if (o.hint) {
      this.hintText = label(scene, this.w - 10, this.h / 2, o.hint, { font: 'mono', size: 11, color: COLORS.uiDim, originX: 1, originY: 0.5 });
      this.root.add(this.hintText);
    }
    const zone = scene.add.zone(0, 0, this.w, this.h).setOrigin(0, 0).setInteractive({ useHandCursor: true });
    zone.on('pointerover', () => {
      this.hover = true;
      this.redraw();
    });
    zone.on('pointerout', () => {
      this.hover = false;
      this.redraw();
    });
    zone.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev?.stopPropagation?.();
      if (this._enabled) this.onClick();
    });
    this.root.add(zone);
    this.redraw();
  }

  get active(): boolean {
    return this._active;
  }

  setActive(on: boolean): this {
    this._active = on;
    this.redraw();
    return this;
  }

  setEnabled(on: boolean): this {
    this._enabled = on;
    this.redraw();
    return this;
  }

  setText(t: string): this {
    this.text.setText(t);
    return this;
  }

  setAccent(c: number): this {
    this.accent = c;
    this.redraw();
    return this;
  }

  private redraw(): void {
    const g = this.bg;
    g.clear();
    const lit = this._active;
    const fill = lit ? this.accent : COLORS.uiPanel;
    const fillAlpha = lit ? 0.22 : this.hover ? 0.95 : 0.8;
    drawPanel(g, 0, 0, this.w, this.h, {
      fill,
      fillAlpha,
      chamfer: 7,
      border: lit || this.hover ? this.accent : COLORS.uiBorder,
      borderAlpha: this._enabled ? 1 : 0.4,
    });
    if (this.o.toggle) {
      // Status pip on the left edge.
      g.fillStyle(lit ? this.accent : COLORS.steel3, lit ? 1 : 0.8);
      g.fillRect(3, 8, 3, this.h - 16);
    }
    this.text.setColor(css(!this._enabled ? COLORS.steel3 : lit ? COLORS.uiText : this.hover ? COLORS.uiText : COLORS.uiDim));
  }
}

/** "< value >" row: label on the left, arrows cycle through values. */
export class Cycler<T> {
  readonly root: Phaser.GameObjects.Container;
  private valueText: Phaser.GameObjects.Text;
  private index = 0;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    w: number,
    title: string,
    private values: readonly T[],
    private format: (v: T) => string,
    private onChange: (v: T) => void,
    accent: number = COLORS.volt,
  ) {
    this.root = scene.add.container(x, y);
    const g = scene.add.graphics();
    drawPanel(g, 0, 0, w, 52, { fill: COLORS.uiBg, fillAlpha: 0.7, chamfer: 6 });
    const t = label(scene, 10, 6, title, { font: 'display', size: 11, color: COLORS.uiDim, spacing: 2 });
    this.valueText = label(scene, w / 2, 33, '', { font: 'body', size: 18, weight: 700, originX: 0.5, originY: 0.5 });
    const prev = new Button(scene, 4, 18, '<', () => this.step(-1), { w: 30, h: 30, accent, size: 16 });
    const next = new Button(scene, w - 34, 18, '>', () => this.step(1), { w: 30, h: 30, accent, size: 16 });
    this.root.add([g, t, this.valueText, prev.root, next.root]);
    this.refresh();
  }

  get value(): T {
    return this.values[this.index];
  }

  step(d: number): void {
    if (this.values.length === 0) return;
    this.index = (this.index + d + this.values.length) % this.values.length;
    this.refresh();
    this.onChange(this.value);
  }

  /** Set without firing onChange. */
  set(v: T): void {
    const i = this.values.indexOf(v);
    if (i >= 0) this.index = i;
    this.refresh();
  }

  private refresh(): void {
    const v = this.values[this.index];
    this.valueText.setText(v === undefined ? '-' : this.format(v));
  }
}

/** Section heading inside the side panel. */
export function sectionTitle(scene: Phaser.Scene, x: number, y: number, text: string, accent: number): Phaser.GameObjects.GameObject[] {
  const t = label(scene, x, y, text, { font: 'display', size: 12, color: COLORS.uiDim, spacing: 3 });
  const g = scene.add.graphics();
  g.fillStyle(accent, 0.9);
  g.fillRect(x - 10, y + 3, 3, 10);
  return [t, g];
}

/** Human label for ids: 'storm_capacitor' → 'STORM CAPACITOR'. */
export function idLabel(id: string | null | undefined): string {
  if (!id) return 'NONE';
  return id.replace(/_/g, ' ').toUpperCase();
}

/** A deterministic placeholder texture (striped box with a cross) for missing art. */
export function placeholderTexture(scene: Phaser.Scene, key = 'pv_missing', w = 96, h = 96, color: number = COLORS.threat): string {
  if (scene.textures.exists(key)) return key;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return '__MISSING';
  const g = tex.context;
  g.fillStyle = css(COLORS.uiPanel);
  g.fillRect(0, 0, w, h);
  g.strokeStyle = css(color, 0.8);
  g.lineWidth = 2;
  g.strokeRect(1, 1, w - 2, h - 2);
  g.beginPath();
  g.moveTo(0, 0);
  g.lineTo(w, h);
  g.moveTo(w, 0);
  g.lineTo(0, h);
  g.stroke();
  tex.refresh();
  return key;
}
